type ServiceWorkerConfig = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

const SERVICE_WORKER_PATH = "/service-worker.js";
const CACHE_PREFIX = "warehouse-static-";

function getServiceWorkerScriptUrl(): string {
  const version = String(import.meta.env.VITE_SW_VERSION || "").trim();
  if (!version) {
    return SERVICE_WORKER_PATH;
  }

  const params = new URLSearchParams({ v: version });
  return `${SERVICE_WORKER_PATH}?${params.toString()}`;
}

async function clearManagedCaches(): Promise<void> {
  if (!("caches" in window)) {
    return;
  }

  try {
    const keys = await caches.keys();
    await Promise.all(
      keys
        .filter((key) => key.startsWith(CACHE_PREFIX))
        .map((key) => caches.delete(key)),
    );
  } catch (error) {
    console.error("Ошибка очистки service worker cache:", error);
  }
}

function isRegistrationAllowed(): boolean {
  if (import.meta.env.PROD) {
    return true;
  }

  const flag = String(import.meta.env.VITE_ENABLE_SW_DEV || "")
    .trim()
    .toLowerCase();
  return flag === "true";
}

export async function register(config?: ServiceWorkerConfig): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  if (!isRegistrationAllowed()) {
    unregister();
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register(
      getServiceWorkerScriptUrl(),
    );

    if (registration.waiting && config?.onUpdate) {
      config.onUpdate(registration);
    }

    registration.onupdatefound = () => {
      const installingWorker = registration.installing;
      if (!installingWorker) {
        return;
      }

      installingWorker.onstatechange = () => {
        if (installingWorker.state !== "installed") {
          return;
        }

        if (navigator.serviceWorker.controller) {
          config?.onUpdate?.(registration);
          return;
        }

        config?.onSuccess?.(registration);
      };
    };
  } catch (error) {
    console.error("Ошибка регистрации service worker:", error);
  }
}

export function unregister(): void {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  void navigator.serviceWorker
    .getRegistrations()
    .then((registrations) =>
      Promise.all(
        registrations.map((registration) => registration.unregister()),
      ),
    )
    .then(() => clearManagedCaches())
    .catch((error) => {
      console.error("Ошибка удаления service worker:", error);
    });
}
